import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { addDays, format } from 'date-fns';
import {
  FieldData,
  NameOfFrequency,
  TextField,
  Select,
  DatepickerSimple,
  Timestamp,
  Tooltip,
  IsTodayDate,
  Flag,
  withStyles,
} from 'vopay';
import { useClientData } from '@/providers/ClientData.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import { useI18nLabels } from '@/utils/useI18nLabels';
import { MyAccountFormData } from '../types';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';

const cn = withStyles(styles);

const cadOnlyOptions = [{ value: 'CAD', label: 'CAD' }];
const usdOnlyOptions = [{ value: 'USD', label: 'USD' }];
const cadUsdOptions = [
  { value: 'CAD', label: 'CAD' },
  { value: 'USD', label: 'USD' },
];

export const useFundAccountFields = () => {
  const {
    response: { USDEFTSendEnabled, clientAccountsEnabled, TransactionLabelOverrideEnabled, Country },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.FundMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { frequencyLabelMapper } = useI18nLabels();
  const { setNewCurrencyInfo } = useAccountInfo();
  const now = format(new Date(), 'yyyy-MM-dd');

  const frequencyOptions = (
    [
      'daily',
      'weekly',
      'bi-weekly',
      'monthly',
      'semi-monthly',
      'quarterly',
      'semi-annually',
      'annually',
      'weekdays',
    ] as NameOfFrequency[]
  ).map((v) => ({ value: v, label: frequencyLabelMapper(v) }));

  const scheduleTypeOptions = [
    { value: 'single', label: tGlobal('ScheduleType.single') },
    { value: 'recurring', label: tGlobal('ScheduleType.recurring') },
  ];

  const endingOptions = [
    { value: 'End Date', label: tGlobal('EndingOptions.endDate') },
    { value: 'Payments', label: tGlobal('EndingOptions.payments') },
  ];

  const semiMonthlyDateOptions = [
    { value: 'SemiMonthFirstAndFifteenth', label: tGlobal('SemiMonthlyDateOptions.firstAndFifteenth') },
    { value: 'SemiMonthFifteenthAndLast', label: tGlobal('SemiMonthlyDateOptions.fifteenthAndLast') },
  ];

  const isBankEligible = (ctx: Partial<MyAccountFormData>) =>
    (Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD');

  const accountFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('Account.label', { defaultValue: 'Account' }),
      fieldName: 'Account',
      getInputState: () => 'required',
      showField: () => !clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            readOnly
            placeholder={t('Account.placeholder', { defaultValue: 'Please Select...' })}
            {...fieldData}
          />
        );
      },
      validate: () => (!clientAccountsEnabled ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('ClientAccount.label', { defaultValue: 'Client Account' }),
      fieldName: 'ClientAccount',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            readOnly
            placeholder={t('Account.placeholder', { defaultValue: 'Please Select...' })}
            {...fieldData}
          />
        );
      },
      validate: () => (clientAccountsEnabled ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('paymentMethod', { defaultValue: 'Payment Method' }),
      fieldName: 'PaymentMethod',
      getInputState: () => 'required',
      showField: () => clientAccountsEnabled,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" readOnly {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
  ];

  const amountFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('amount', { defaultValue: 'Amount' }),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="currency" placeholder="0.00" {...fieldData} />;
      },
      validate: () =>
        Yup.string()
          .test(
            'amount-validation',
            t('ErrorMessage.amountMinValue', {
              defaultValue: 'This value should be greater than or equal to $0.01.',
            }),
            (value) => Number(value) > 0,
          )
          .required(),
    },
    {
      label: () => <span>&nbsp;</span>,
      fieldName: 'Currency',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const currencyOpts =
          Country === 'US' ? usdOnlyOptions : USDEFTSendEnabled === '0' ? cadOnlyOptions : cadUsdOptions;
        return (
          <Select
            version="2"
            options={currencyOpts}
            {...fieldData}
            onChange={(e) => {
              setNewCurrencyInfo(e.target.value);
              fieldData.onChange?.(e);
            }}
            renderSelectedValue={(o, { disabled }) => (
              <div className={cn('currencyOption')}>
                <Flag className={cn(disabled && 'iconDisabled')} flag={o?.value || ''} width="24px" />
                {o?.label}
              </div>
            )}
            renderOption={(o, { Option }) => (
              <Option style={{ justifyContent: 'flex-start' }}>
                <div className={cn('currencyOption')}>
                  <Flag flag={o.value} width="24px" /> {o.label}
                </div>
              </Option>
            )}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];

  const scheduleFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('scheduleType', { defaultValue: 'Schedule Type' }),
      fieldName: 'ScheduleType',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" options={scheduleTypeOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'Currency'], {
          is: (PaymentMethod: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('TakeFromAccountOn.label', { defaultValue: 'Funding Date' }),
      fieldName: 'TakeFromAccountOn',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType !== 'recurring',
      tooltip: () => (
        <Tooltip
          type="hover"
          information={t('TakeFromAccountOn.toolTip', {
            defaultValue:
              'The payment/collection transaction will be initiated immediately after the user has connected their bank account.',
          })}
          position="right"
          hasArrow={false}
          icon="question"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={new Date().getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'single' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string()
            .test(
              'take-from-account-validation',
              t('ErrorMessage.takeFromAccountOnInvalidStartDate', {
                startDate: now,
                defaultValue: 'Invalid date please choose a date on or after {{startDate}}',
              }),
              (value) => !(value !== 'undefined' && format(new Date(Number(value)), 'yyyy-MM-dd') < now),
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('startDate', { defaultValue: 'Start Date' }),
      fieldName: 'StartDate',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={addDays(new Date(), 1).getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'recurring' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string()
            .test(
              'start-date-validation',
              t('ErrorMessage.invalidStartDate', {
                startDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                defaultValue: 'Invalid date. Please choose a date on or after {{startDate}}',
              }),
              (value) =>
                !(
                  value !== 'undefined' &&
                  format(new Date(Number(value)), 'yyyy-MM-dd') < format(addDays(new Date(), 1), 'yyyy-MM-dd')
                ),
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('frequency', { defaultValue: 'Frequency' }),
      fieldName: 'Frequency',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" options={frequencyOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'recurring' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('semiMonthlyDates', { defaultValue: 'Semi-Monthly Dates' }),
      fieldName: 'SemiMonthlyDates',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" options={semiMonthlyDateOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Frequency', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Frequency: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'recurring' &&
            Frequency === 'semi-monthly' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('ending', { defaultValue: 'Ending' }),
      fieldName: 'Ending',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType === 'recurring',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" options={endingOptions} {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'recurring' &&
            ((Country === 'CA' && Currency === 'CAD') || (Country === 'US' && Currency === 'USD')),
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('endDate', { defaultValue: 'End Date' }),
      fieldName: 'EndDate',
      getInputState: () => 'required',
      showField: (ctx) => isBankEligible(ctx) && ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <DatepickerSimple
            {...fieldData}
            variant="outlineShadow"
            placeholder="YYYY-MM-DD"
            iconPosition="left"
            min={addDays(new Date(Number(ctx.values.StartDate)), 1).getTime() as Timestamp}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending', 'Currency'], {
          is: (PaymentMethod: string, ScheduleType: string, Ending: string, Currency: string) =>
            PaymentMethod === 'Bank Account' &&
            ScheduleType === 'recurring' &&
            Ending === 'End Date' &&
            Country === 'CA' &&
            Currency === 'CAD',
          then: Yup.string()
            .test(
              'end-date-validation',
              t('ErrorMessage.invalidEndDate', {
                defaultValue: 'Invalid date. Please choose a date on or after start date',
              }),
              function (value) {
                if (!value || !this.parent?.StartDate) return true;
                const endDate = new Date(format(new Date(Number(value)), 'yyyy-MM-dd'));
                const startDate = new Date(format(new Date(Number(this.parent.StartDate)), 'yyyy-MM-dd'));
                return endDate > startDate;
              },
            )
            .required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('endingAfterPayments', { defaultValue: 'Ending After Payments' }),
      fieldName: 'EndingAfterPayments',
      getInputState: () => 'required',
      showField: (ctx) =>
        ctx.Ending === 'Payments' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Ending'], {
          is: (PaymentMethod: string, ScheduleType: string, Ending: string) =>
            PaymentMethod === 'Bank Account' && ScheduleType === 'recurring' && Ending === 'Payments',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('Description.label', { defaultValue: 'Description' }),
      fieldName: 'Description',
      getInputState: () => 'required',
      showField: (ctx) =>
        (ctx.ScheduleType === 'recurring' ||
          (!!ctx.TakeFromAccountOn &&
            ctx.TakeFromAccountOn !== '' &&
            !IsTodayDate(format(new Date(Number(ctx.TakeFromAccountOn)), 'yyyy-MM-dd')))) &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="text"
            placeholder={t('Description.placeholder', { defaultValue: 'Schedule Description' })}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string().when(['PaymentMethod', 'ScheduleType', 'Currency', 'TakeFromAccountOn'], {
          is: (PaymentMethod: string, ScheduleType: string, Currency: string, TakeFromAccountOn: string) =>
            PaymentMethod === 'Bank Account' &&
            (ScheduleType === 'recurring' ||
              (TakeFromAccountOn && !IsTodayDate(format(new Date(Number(TakeFromAccountOn)), 'yyyy-MM-dd')))) &&
            Country === 'CA' &&
            Currency === 'CAD',
          then: Yup.string().required(),
          otherwise: Yup.string(),
        }),
    },
    {
      label: () => t('messageForRecipient', { defaultValue: 'Message For Recipient' }),
      fieldName: 'MessageForRecipient',
      getInputState: () => 'optional',
      showField: (ctx) => ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
    },
  ];

  const additionalFields: FieldData<MyAccountFormData>[] = [
    {
      label: () => t('transactionLabel', { defaultValue: 'Transaction Label' }),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: (ctx) => TransactionLabelOverrideEnabled === '1' && ctx.PaymentMethod === 'Bank Account',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" maxLength={30} {...fieldData} />;
      },
      validate: () =>
        Yup.string().max(
          30,
          t('ErrorMessage.transactionLabelMaximumLength', {
            defaultValue: 'The value supplied exceeds the maximum length (max. 30)',
          }),
        ),
    },
    {
      label: () => t('notes', { defaultValue: 'Notes' }),
      fieldName: 'Notes',
      getInputState: () => 'optional',
      showField: (ctx) => ctx.PaymentMethod !== 'Credit Card',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" preset="text" {...fieldData} />;
      },
    },
  ];

  return { accountFields, amountFields, scheduleFields, additionalFields };
};
